import { BufferAttribute, Color, MeshStandardMaterial, Skeleton, Vector3, type Bone, type BufferGeometry, type Material, type Object3D, type SkinnedMesh } from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { addRim, RIM } from './materials';
import type { ArmorMaterial, Slot } from '@gra/content';

/**
 * Gołe ciało bohatera na manekinie UAL: skóra rasy, lniana bielizna i włosy jako osobne strefy siatki.
 * Zbroja na tułów, nogi, dłonie i stopy zmienia materiał swoich stref (kolor, połysk, lekkie pogrubienie),
 * więc bez ekwipunku postać nie ma na sobie nic poza bielizną.
 */

export type Region = 'head' | 'hair' | 'torso' | 'band' | 'arms' | 'forearms' | 'hands' | 'hips' | 'thighs' | 'shins' | 'feet';
const REGIONS: Region[] = ['head', 'hair', 'torso', 'band', 'arms', 'forearms', 'hands', 'hips', 'thighs', 'shins', 'feet'];

export interface BodyLook {
  skinTone: string;
  hair: string;
  /** [szerokość, wysokość] względem człowieka */
  build: [number, number];
  female: boolean;
  /**
   * Ciało z AI (race-models.json) ma własne tekstury skóry i bielizny: strefy bez zbroi zostają przy materiale
   * modelu, a proporcje rasy są już w modelu. Manekin dostaje kolory i proporcje z `look`.
   */
  textured?: boolean;
}

export interface ArmorVisual {
  slot: Slot;
  color: string;
  material: ArmorMaterial;
  trim?: string;
  /** Kolor rzadkości — delikatna poświata zbroi od rzadkich wzwyż. */
  rarity: string;
  tier: number;
  /** Plik modelu części zbroi (armor-models.json) — gdy jest, zakładamy go zamiast koloru strefy. */
  modelPath?: string;
  /** Wczytany model części (uzupełnia CharacterModel z Assets). */
  model?: Object3D;
}

/** Które strefy ciała zakrywa zbroja w danym slocie. */
const COVER: Partial<Record<Slot, Region[]>> = {
  chest: ['torso', 'band', 'arms'],
  legs: ['hips', 'thighs', 'shins'],
  hands: ['hands', 'forearms'],
  feet: ['feet'],
};

/**
 * Które strefy chowa model części zbroi (reszta strefy z COVER zostaje skórą). Brzeg napierśnika czy spodni z AI
 * bywa falisty i nie sięga granicy strefy — schowany tułów albo biodra dawały prześwit na wylot, więc zostają pod
 * spodem, lekko wciągnięte (UNDER). Rękawica i but zawsze zakrywają całą dłoń i stopę.
 */
const HIDE: Partial<Record<Slot, Region[]>> = {
  hands: ['hands'],
  feet: ['feet'],
};
/**
 * Ciała ras (Universal Base Characters, proporcje „Superhero”) są masywniejsze niż ciało, pod które szyto stroje
 * (build-outfits.ts) — pod częścią stroju chowamy całą zakrytą strefę, inaczej mięśnie przebijają materiał.
 * Karwasze stroju mają całe ręce z dłońmi, buty sięgają za łydkę.
 */
const HIDE_TEXTURED: Partial<Record<Slot, Region[]>> = {
  chest: ['torso', 'band'],
  legs: ['hips', 'thighs'],
  hands: ['hands', 'forearms', 'arms'],
  feet: ['feet', 'shins'],
};

/** Strefy skóry wciągane pod modelem części zbroi; cholewa buta sięga na łydkę (tylko do swojej wysokości). */
const UNDER: Partial<Record<Slot, Region[]>> = {
  chest: ['torso', 'band', 'arms'],
  legs: ['hips', 'thighs', 'shins'],
  hands: ['hands', 'forearms'],
  feet: ['feet', 'shins'],
};

const LINEN = '#b8a07c';
/** O ile skóra pod modelem zbroi chowa się w głąb (jednostki manekina) — inaczej klatka czy łydki przebijają zbroję. */
const UNDER_ARMOR = 0.03;
/** Ciała z AI są masywniejsze niż manekin, do którego asset:character --part dopasowuje zbroję — skóra chowa się głębiej. */
const UNDER_ARMOR_TEXTURED = 0.06;

/** Kość → strefa (nazwy po sanityzacji GLTFLoadera: "DEF-spine.001" → "DEF-spine001"). */
function boneRegion(name: string): Region {
  if (/thumb|f_(index|middle|ring|pinky)|DEF-hand/.test(name)) return 'hands';
  if (/forearm/.test(name)) return 'forearms';
  if (/upper_arm|shoulder/.test(name)) return 'arms';
  if (/DEF-head|DEF-neck/.test(name)) return 'head';
  if (/thigh/.test(name)) return 'thighs';
  if (/shin/.test(name)) return 'shins';
  if (/foot|toe/.test(name)) return 'feet';
  if (/spine/.test(name)) return 'torso';
  return 'hips';
}

const PRESET: Record<ArmorMaterial, { roughness: number; metalness: number; inflate: number }> = {
  // Bez mapy otoczenia mocno metaliczny materiał wychodzi prawie czarny — połowiczny metal z jaśniejszym kolorem.
  metal: { roughness: 0.38, metalness: 0.45, inflate: 0.012 },
  leather: { roughness: 0.62, metalness: 0.05, inflate: 0.008 },
  fur: { roughness: 1, metalness: 0, inflate: 0.016 },
  cloth: { roughness: 0.9, metalness: 0, inflate: 0.005 },
};

/**
 * Materiał strefy: przesunięcie wierzchołków wzdłuż normalnej (zbroja leży na skórze, nie jest farbą)
 * i światło konturowe jak u pozostałych postaci (materials.ts → addRim).
 */
function regionMaterial(base?: Material): MeshStandardMaterial {
  const m = base ? ((base as MeshStandardMaterial).clone() as MeshStandardMaterial) : new MeshStandardMaterial({ roughness: 0.75, metalness: 0 });
  m.userData.inflate = { value: 0 };
  // Wciąganie skóry (inflate < 0) tylko poniżej tej wysokości w układzie wiązania szkieletu — np. do górnej krawędzi cholewy.
  m.userData.inflateTop = { value: 1e9 };
  m.onBeforeCompile = (shader) => {
    shader.uniforms.inflate = m.userData.inflate;
    shader.uniforms.inflateTop = m.userData.inflateTop;
    shader.uniforms.uRimColor = RIM.color;
    shader.uniforms.uRimStrength = RIM.strength;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float inflate;\nuniform float inflateTop;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        #ifdef USE_SKINNING
        float bindY = (bindMatrix * vec4(position, 1.0)).y;
        #else
        float bindY = position.y;
        #endif
        if (inflate > 0.0 || (inflate < 0.0 && bindY < inflateTop)) transformed += normalize(objectNormal) * inflate;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uRimColor;\nuniform float uRimStrength;')
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        float rimDot = 1.0 - saturate(dot(normal, normalize(vViewPosition)));
        totalEmissiveRadiance += uRimColor * pow(rimDot, 3.0) * uRimStrength;`,
      );
  };
  m.customProgramCacheKey = () => 'rim-inflate';
  return m;
}

interface RegionMats {
  /** Skóra / bielizna / włosy (u ciała z AI — klon materiału modelu z teksturą). */
  bare: MeshStandardMaterial[];
  /** Zbroja bez modelu: kolor i materiał z danych przedmiotu. */
  armor: MeshStandardMaterial[];
}

/** Siatka ciała podzielona na strefy: grupy trójkątów po strefach + osobne materiały dla każdej strefy. */
function splitRegions(mesh: SkinnedMesh, female: boolean, textured: boolean): RegionMats {
  const geo = (mesh.geometry as BufferGeometry).clone();
  const bones = mesh.skeleton.bones;
  const pos = geo.getAttribute('position') as BufferAttribute;
  const skinIndex = geo.getAttribute('skinIndex') as BufferAttribute;
  const skinWeight = geo.getAttribute('skinWeight') as BufferAttribute;
  const count = pos.count;
  const region = new Array<Region>(count);
  for (let v = 0; v < count; v++) {
    let best = 0;
    let w = -1;
    for (let k = 0; k < 4; k++) {
      const wk = skinWeight.getComponent(v, k);
      if (wk > w) {
        w = wk;
        best = skinIndex.getComponent(v, k);
      }
    }
    region[v] = boneRegion(bones[best]?.name ?? '');
  }
  // Włosy: górna i tylna część głowy; opaska na piersi u postaci żeńskiej.
  const bounds = (r: Region) => {
    let min = Infinity, max = -Infinity, zMin = Infinity, zMax = -Infinity;
    for (let v = 0; v < count; v++) {
      if (region[v] !== r) continue;
      const y = pos.getY(v), z = pos.getZ(v);
      min = Math.min(min, y); max = Math.max(max, y);
      zMin = Math.min(zMin, z); zMax = Math.max(zMax, z);
    }
    return { min, max, zMin, zMax };
  };
  const head = bounds('head');
  const torso = bounds('torso');
  const tmp = new Vector3();
  for (let v = 0; v < count; v++) {
    tmp.fromBufferAttribute(pos, v);
    if (region[v] === 'head') {
      // Kierunek od środka głowy (znormalizowany do rozmiaru): góra i tył to włosy. Manekin patrzy w +Z.
      const y = ((tmp.y - head.min) / (head.max - head.min)) * 2 - 1;
      const z = ((tmp.z - head.zMin) / (head.zMax - head.zMin)) * 2 - 1;
      if (y > 0.35 || (z < -0.15 && y > (female ? -0.9 : -0.35))) region[v] = 'hair';
    } else if (female && region[v] === 'torso') {
      const h = (tmp.y - torso.min) / (torso.max - torso.min);
      if (h > 0.5 && h < 0.82) region[v] = 'band';
    }
  }
  // Trójkąty do grup według strefy pierwszego wierzchołka z większości.
  const index = geo.getIndex()!;
  const buckets = REGIONS.map(() => [] as number[]);
  for (let t = 0; t < index.count; t += 3) {
    const a = index.getX(t), b = index.getX(t + 1), c = index.getX(t + 2);
    const ra = region[a]!, rb = region[b]!, rc = region[c]!;
    const r = ra === rb || ra === rc ? ra : rb === rc ? rb : ra;
    buckets[REGIONS.indexOf(r)]!.push(a, b, c);
  }
  const flat: number[] = [];
  geo.clearGroups();
  buckets.forEach((tris, i) => {
    if (tris.length) geo.addGroup(flat.length, tris.length, i);
    flat.push(...tris);
  });
  geo.setIndex(flat);
  mesh.geometry = geo;
  const original = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
  const mats = { bare: REGIONS.map(() => regionMaterial(textured ? original : undefined)), armor: REGIONS.map(() => regionMaterial()) };
  mesh.material = [...mats.bare];
  return mats;
}

/**
 * Ciało bohatera: dzieli siatki ciała na strefy i trzyma ich materiały, żeby zbroja mogła je zmieniać.
 */
export class HeroBody {
  private readonly meshes: { mesh: SkinnedMesh; mats: RegionMats }[] = [];
  private readonly look: BodyLook;
  private readonly bones = new Map<string, Bone>();
  private parts: SkinnedMesh[] = [];

  constructor(body: Object3D, look: BodyLook) {
    this.look = look;
    body.traverse((o) => {
      if ((o as Bone).isBone) this.bones.set(o.name, o as Bone);
      const mesh = o as SkinnedMesh;
      if (!mesh.isSkinnedMesh) return;
      this.meshes.push({ mesh, mats: splitRegions(mesh, look.female, !!look.textured) });
    });
    // Proporcje rasy: szerzej/węziej i wyżej/niżej (cała postać, animacje działają bez zmian).
    // Ciała ras (Universal Base Characters) to jedno ciało dla wszystkich ras — proporcje nadajemy tutaj.
    body.scale.multiply(new Vector3(look.build[0], look.build[1], look.build[0]));
    this.setArmor([]);
  }

  /** Nakłada zbroję na strefy; strefy bez zbroi wracają do skóry, bielizny i włosów. */
  setArmor(armor: ArmorVisual[]): void {
    for (const p of this.parts) p.removeFromParent();
    this.parts = [];
    const covered = new Map<Region, ArmorVisual>();
    // Z modelem części zbroi zakrywamy tylko strefy pod nim (HIDE); bez modelu — całą strefę kolorem (COVER).
    const hide = this.look.textured ? HIDE_TEXTURED : HIDE;
    for (const a of armor) for (const r of (a.model ? hide : COVER)[a.slot] ?? []) covered.set(r, a);
    // Strefa → do jakiej wysokości wciągnąć skórę (Infinity = cała strefa).
    const under = new Map<Region, number>();
    for (const a of armor) {
      if (!a.model) continue;
      const parts = this.attachPart(a.model, a);
      let top = Infinity;
      if (a.slot === 'feet') {
        top = -Infinity;
        for (const p of parts) {
          p.geometry.computeBoundingBox();
          top = Math.max(top, p.geometry.boundingBox!.clone().applyMatrix4(p.bindMatrix).max.y);
        }
      }
      for (const r of UNDER[a.slot] ?? []) {
        // Ciało z AI ma rozdzielone normalne: częściowe wciągnięcie łydki (do cholewy) rozrywało siatkę na płaty.
        if (this.look.textured && top !== Infinity) continue;
        under.set(r, Math.max(under.get(r) ?? -Infinity, top));
      }
    }
    const skin = new Color(this.look.skinTone);
    REGIONS.forEach((region, i) => {
      const a = covered.get(region);
      for (const { mesh, mats } of this.meshes) {
        const list = mesh.material as Material[];
        if (a) {
          const m = mats.armor[i]!;
          list[i] = m;
          // Model części zakrywa strefę — skóra pod nim byłaby widać przez szczeliny i przy zgięciach.
          m.visible = !a.model;
          const p = PRESET[a.material];
          // Przedramiona i opaska w kolorze wykończenia (np. złote karwasze), jeśli zbroja je ma.
          m.color.set(a.trim && (region === 'forearms' || region === 'band') ? a.trim : a.color);
          m.roughness = p.roughness;
          m.metalness = p.metalness;
          m.userData.inflate.value = p.inflate;
          m.emissive.setRGB(0, 0, 0);
          if (a.tier >= 2) m.emissive.set(a.rarity).multiplyScalar(0.04 * a.tier);
        } else {
          const m = mats.bare[i]!;
          list[i] = m;
          m.userData.inflate.value = under.has(region) ? -(this.look.textured ? UNDER_ARMOR_TEXTURED : UNDER_ARMOR) : 0;
          m.userData.inflateTop.value = Math.min(under.get(region) ?? 1e9, 1e9);
          if (this.look.textured) continue;
          const linen = region === 'hips' || region === 'band';
          m.color.copy(linen ? new Color(LINEN) : region === 'hair' ? new Color(this.look.hair) : skin);
          m.roughness = linen ? 0.92 : region === 'hair' ? 0.85 : 0.62;
          m.metalness = 0;
          if (!under.has(region)) m.userData.inflate.value = linen ? 0.004 : region === 'hair' ? 0.006 : 0;
        }
      }
    });
  }

  /**
   * Model części zbroi na tym samym szkielecie: klon przepięty na kości bohatera (po nazwach).
   * Części strojów mają szare tekstury i nazwane materiały: „stroj” dostaje kolor przedmiotu, „stroj-pas” kolor
   * wykończenia, „skora” (dłonie w karwaszach) kolor skóry rasy.
   */
  private attachPart(template: Object3D, a: ArmorVisual): SkinnedMesh[] {
    const host = this.meshes[0]?.mesh;
    if (!host) return [];
    const added: SkinnedMesh[] = [];
    const clone = SkeletonUtils.clone(template);
    clone.traverse((o) => {
      const part = o as SkinnedMesh;
      if (!part.isSkinnedMesh) return;
      const bones = part.skeleton.bones.map((b) => this.bones.get(b.name) ?? b);
      part.bind(new Skeleton(bones, part.skeleton.boneInverses), part.bindMatrix);
      part.frustumCulled = false;
      part.castShadow = true;
      part.receiveShadow = true;
      const mat = part.material;
      part.material = Array.isArray(mat) ? mat.map((m) => this.tintPart(m as MeshStandardMaterial, a)) : this.tintPart(mat as MeshStandardMaterial, a);
      this.parts.push(part);
      added.push(part);
    });
    for (const part of added) host.parent!.add(part);
    return added;
  }

  private tintPart(source: MeshStandardMaterial, a: ArmorVisual): MeshStandardMaterial {
    const m = tintOutfit(source, a, this.look.skinTone);
    // Ta sama obwódka światła (rim) co na ciele — inaczej zbroja odcinała się od tła słabiej niż skóra.
    addRim(m);
    return m;
  }
}

/**
 * Barwienie części stroju (build-outfits.ts): „stroj” — kolor przedmiotu, „stroj-pas” — kolor wykończenia,
 * „skora” (dłonie w karwaszach) — kolor skóry. Wspólne dla postaci i ikon ekwipunku, żeby wyglądały tak samo.
 */
export function tintOutfit(source: MeshStandardMaterial, a: Pick<ArmorVisual, 'color' | 'trim' | 'material' | 'tier' | 'rarity'>, skinTone: string): MeshStandardMaterial {
  const m = source.clone();
  if (m.name === 'skora') {
    m.color.set(skinTone);
    return m;
  }
  if (m.name !== 'stroj' && m.name !== 'stroj-pas') return m;
  const p = PRESET[a.material];
  // Tekstury są przygaszone (średnio ok. 0,62, połowa barw) — ×1,5 daje przy średniej jasności sam kolor przedmiotu
  // (granatowy zostaje granatowy, stal stalą), a faktura, pasy i sprzączki dalej widać.
  m.color.set(m.name === 'stroj-pas' ? (a.trim ?? a.color) : a.color).multiplyScalar(1.5);
  // Stroje mają fakturę skóry i tkaniny — pełny „metal” robił z nich czarny lateks (brak otoczenia do odbicia).
  m.roughness = Math.max(0.45, p.roughness);
  m.metalness = p.metalness * 0.35;
  // Delikatna poświata rzadkości — mocniejsza zagłuszała kolor przedmiotu (epicki granat wychodził fioletowy).
  if (a.tier >= 2) m.emissive.set(a.rarity).multiplyScalar(0.012 * a.tier);
  return m;
}
